import { WebGLTimerQuery } from '../WebGLTimerQuery.js';
export class GpuProfilingSection {
    constructor(profiler, _handle, name) {
        this._last_sample_frame_id = -1;
        // This flag can be set to true if the section is unaffected by the normal viewport resolution, such as for shadow map rendering.
        this.is_resolution_independent = false;
        this.profiler = profiler;
        this._handle = _handle;
        this.name = name;
    }
    start() {
        var _a;
        (_a = this.profiler) === null || _a === void 0 ? void 0 : _a.start_section(this);
    }
    stop() {
        var _a;
        (_a = this.profiler) === null || _a === void 0 ? void 0 : _a.stop_section(this);
    }
    get_sample_count() {
        if (this.profiler === null)
            return NaN;
        let count = 0;
        const samples = this.profiler.get_samples(this);
        for (let i = 0; i < samples.length; i++) {
            if (samples[i] >= 0) {
                count += 1;
            }
        }
        return count;
    }
    get_average_duration() {
        if (this.profiler === null)
            return NaN;
        let sum = 0.0;
        let count = 0;
        const samples = this.profiler.get_samples(this);
        for (let i = 0; i < samples.length; i++) {
            if (samples[i] >= 0) {
                sum += samples[i];
                count += 1;
            }
        }
        return sum / Math.max(1, count);
    }
    get_average_cpu_duration() {
        if (this.profiler === null)
            return NaN;
        let sum = 0.0;
        let count = 0;
        const samples = this.profiler.get_cpu_samples(this);
        for (let i = 0; i < samples.length; i++) {
            if (samples[i] >= 0) {
                sum += samples[i];
                count += 1;
            }
        }
        return sum / Math.max(1, count);
    }
    get_average_cpu_load() {
        if (this.profiler === null)
            return NaN;
        let sum = 0.0;
        let count = 0;
        const samples_gpu = this.profiler.get_samples(this);
        const samples_cpu = this.profiler.get_cpu_samples(this);
        for (let i = 0; i < samples_gpu.length; i++) {
            if (samples_gpu[i] >= 0 && samples_cpu[i] >= 0) {
                sum += samples_cpu[i] / samples_gpu[i];
                count += 1;
            }
        }
        return sum / Math.max(1, count);
    }
    clear_results() {
        if (this.profiler === null)
            return;
        const samples = this.profiler.get_samples(this);
        const cpu = this.profiler.get_cpu_samples(this);
        for (let i = 0; i < samples.length; i++) {
            samples[i] = -1.0;
            cpu[i] = -1.0;
        }
    }
}
export class GpuProfiler {
    // Returns the current CPU timestamp in nanoseconds
    _get_cpu_timestamp() {
        // Multiply by 1 million to get nanoseconds, the same unit as the WebGL timer queries
        return performance.now() * 1000000.0;
    }
    static is_supported(gl) {
        return WebGLTimerQuery.is_supported(gl);
    }
    constructor(gl, average_sample_count) {
        this._active_profiling_section = null;
        this._is_profiling = false;
        this._frame_id = 0;
        this._sections_in_frame = 0;
        this._last_render_width = 0;
        this._last_render_height = 0;
        this._update_visualizer_function_handle = null; // this makes sure fps_counters update loop is removed if not enabled.
        this._visualizer_element = null;
        this.visualizer_enable_debug_details = false;
        // How many seconds between visualizer updates
        this.visualizer_render_time_seconds = 1.0;
        this._last_visualizer_update_timestamp = 0;
        this._gl = gl;
        this._sections = [];
        this._timing_average_sampe_count = average_sample_count;
    }
    get_samples(section) {
        return this._sections[section._handle].timer_results;
    }
    get_cpu_samples(section) {
        return this._sections[section._handle].cpu_results;
    }
    create_section(name) {
        const user = new GpuProfilingSection(this, this._sections.length, name);
        const timing_samples = [];
        for (let i = 0; i < this._timing_average_sampe_count; i++) {
            timing_samples.push(-1);
        }
        const cpu_times = [];
        for (let i = 0; i < this._timing_average_sampe_count; i++) {
            cpu_times.push(-1);
        }
        this._sections.push({
            user: user,
            parent: null,
            sample_child_count_accumulator: 0,
            last_child_count: 0,
            timer: new WebGLTimerQuery('GpuProfilingSection_' + name, this._gl),
            timer_is_running: false,
            timer_is_awaiting_result: false,
            timer_results: timing_samples,
            cpu_results: cpu_times,
            cpu_start_timestamp: -1,
            cpu_awaiting_result: -1,
            timer_next_result: 0,
            timer_activated_frame_id: -1,
            last_requested_age: 0
        });
        return user;
    }
    start_frame(render_width, render_height) {
        if (this._last_render_width !== render_width || this._last_render_height !== render_height) {
            // Scrap any existing results to make sure we don't accidentaly read data from incorrect resolutions
            for (let i = 0; i < this._sections.length; i++) {
                if (this._sections[i].user.is_resolution_independent)
                    continue;
                this._sections[i].user.clear_results();
            }
        }
        this._last_render_width = render_width;
        this._last_render_height = render_height;
        this._sections_in_frame = 0;
        this._active_profiling_section = null;
        for (let i = 0; i < this._sections.length; i++) {
            this._sections[i].sample_child_count_accumulator = 0;
            const section = this._sections[i];
            if (section.timer_is_awaiting_result && section.timer.has_result()) {
                section.timer_is_awaiting_result = false;
                const result = section.timer.get_result();
                if (result !== null && result > 0) {
                    section.cpu_results[section.timer_next_result] = section.cpu_awaiting_result;
                    section.timer_results[section.timer_next_result] = result;
                    section.timer_next_result = (section.timer_next_result + 1) % section.timer_results.length;
                    section.user._last_sample_frame_id = section.timer_activated_frame_id;
                }
                section.timer.clear_result();
                section.cpu_awaiting_result = -1;
                section.cpu_start_timestamp = -1;
            }
        }
    }
    end_frame() {
        if (this._sections_in_frame > 0) {
            this._frame_id += 1;
        }
        if (this._active_profiling_section !== null) {
            console.error('Not all profiling sections have ended: ' + this._active_profiling_section.user.name);
        }
        for (let i = 0; i < this._sections.length; i++) {
            this._sections[i].last_child_count = this._sections[i].sample_child_count_accumulator;
        }
    }
    get_summary() {
        let summary = 'GPU Profiler Summary:\n';
        for (let i = 0; i < this._sections.length; i++) {
            summary += this._sections[i].user.name + ': ' + Math.floor(this._sections[i].user.get_average_duration() / 1000.0) + ' us\n';
        }
        return summary;
    }
    start_section(user_section) {
        this._sections_in_frame += 1;
        const section = this._sections[user_section._handle];
        section.parent = this._active_profiling_section;
        this._active_profiling_section = section;
        if (section.timer_is_awaiting_result) {
            // We're already awaiting a result, so timer query is occupied.
            return;
        }
        const parent = section.parent;
        if (parent !== null) {
            parent.sample_child_count_accumulator += 1;
        }
        if (this._is_profiling) {
            return;
        }
        // Determine if we should profile, and if so start it.
        // Stochastically determine if we should profile ourselves, or if one of our children should get the chance.
        const profile = Math.random() < 1.0 / Math.max(1.0, section.last_child_count);
        if (profile) {
            this._is_profiling = true;
            section.timer_is_running = true;
            section.timer_activated_frame_id = this._frame_id;
            section.timer.start_timer();
            section.cpu_start_timestamp = this._get_cpu_timestamp();
        }
    }
    stop_section(user_section) {
        if (this._active_profiling_section !== null) {
            this._active_profiling_section = this._active_profiling_section.parent;
        }
        const section = this._sections[user_section._handle];
        if (section.timer_is_running) {
            section.cpu_awaiting_result = this._get_cpu_timestamp() - section.cpu_start_timestamp;
            section.timer_is_running = false;
            section.timer_is_awaiting_result = true;
            section.timer.stop_timer();
            this._is_profiling = false;
        }
    }
    dispose() {
        this._update_visualizer_function_handle = null;
        if (this._visualizer_element !== null) {
            this._visualizer_element.innerHTML = '';
            document.body.removeChild(this._visualizer_element);
            this._visualizer_element = null;
        }
        for (let i = 0; i < this._sections.length; i++) {
            this._sections[i].user.profiler = null;
            this._sections[i].timer.dispose();
        }
        this._sections = [];
    }
    get_data_as_csv() {
        let csv = '';
        csv += 'section,average,sample_count,age\n';
        for (let i = 0; i < this._sections.length; i++) {
            const section = this._sections[i];
            const user = section.user;
            csv += user.name.replace(',', '') + ',' + user.get_average_duration() + ',' + user.get_sample_count() + ',' + (user._last_sample_frame_id >= 0 ? (this._frame_id - user._last_sample_frame_id).toFixed(0) : -1) + '\n';
        }
        return csv;
    }
    enable_visualizer(viewer, enable) {
        if (this._visualizer_element === null) {
            this._visualizer_element = document.createElement('div');
            this._visualizer_element.style.zIndex = '10';
            this._visualizer_element.style.position = 'absolute';
            this._visualizer_element.style.left = '0.5em';
            this._visualizer_element.style.top = '3em';
            this._visualizer_element.style.textAlign = 'left';
            this._visualizer_element.style.fontFamily = 'monospace';
            this._visualizer_element.style.fontSize = '12px';
            this._visualizer_element.style.userSelect = 'none';
            this._visualizer_element.style.pointerEvents = 'none';
            this._visualizer_element.style.background = 'rgba(111, 222, 55, 0.95)';
            document.body.appendChild(this._visualizer_element);
        }
        if (enable) {
            this._visualizer_element.style.display = 'block';
            this._update_visualizer_function_handle = requestAnimationFrame(this.update_visualizer.bind(this));
        }
        else {
            this._visualizer_element.style.display = 'none';
            if (this._update_visualizer_function_handle !== null) {
                cancelAnimationFrame(this._update_visualizer_function_handle);
            }
            this._update_visualizer_function_handle = null;
        }
    }
    update_visualizer() {
        // This code is a real eye-sore, and we should find a better way to do this in the future.
        // Convert nanoseconds to seconds
        const timestamp = this._get_cpu_timestamp() / 1000000000.0;
        if (timestamp - this._last_visualizer_update_timestamp >= this.visualizer_render_time_seconds) {
            this._last_visualizer_update_timestamp = timestamp;
            const style = `
        border-spacing: 32px 6px;
      `;
            let html = '<table style="' + style + '"><tr>';
            html += '<th>GPU Profiler (' + this._last_render_width + 'x' + this._last_render_height + ')</th><th>Avg GPU</th><th>Avg CPU</th><th>#/A</th>';
            if (this.visualizer_enable_debug_details) {
                html += '<th>Local Rate</th>';
            }
            html += '</tr>';
            for (let i = 0; i < this._sections.length; i++) {
                html += '<tr><td>' + this._sections[i].user.name + '</td>';
                html += '<td><div style="text-align: left; float: left;">' + Math.floor(this._sections[i].user.get_average_duration() / 1000.0) + ' </div><div style="text-align: right; float: right;">μs</div></td>';
                const cpu_load = this._sections[i].user.get_average_cpu_load();
                html += '<td style="background: rgba(255, 32, 32, ' + cpu_load + ')"><div style="text-align: left; float: left;">' + Math.floor(cpu_load * 100.0) + ' </div><div style="text-align: right; float: right;">%</div></td>';
                const age = this._sections[i].user._last_sample_frame_id < 0 ? '?' : (this._frame_id - this._sections[i].user._last_sample_frame_id);
                html += '<td>' + this._sections[i].user.get_sample_count() + '/' + age + '</td>';
                if (this.visualizer_enable_debug_details) {
                    html += '<td>' + (100.0 / Math.max(1, this._sections[i].last_child_count)).toFixed(0) + ' %</td>';
                }
                html += '</tr>';
            }
            html += '</table>';
            this._visualizer_element.innerHTML = html;
        }
        if (this._update_visualizer_function_handle) {
            this._update_visualizer_function_handle = requestAnimationFrame(this.update_visualizer.bind(this));
        }
    }
}
//# sourceMappingURL=GpuProfiler.js.map